import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ROAST_ATTR_TAXONOMY } from "@/constants/common";
import { SortDirection, SortOrderBy } from "@/constants/enums";
import type { ProductsQuery } from "@/viewModels/shop/features/useFilter/types";

interface UseShopFilterStateReturn {
  getFilterFromURL: () => ProductsQuery | null;
  saveFilterToURL: (filter: ProductsQuery) => void;
}

/**
 * 商店頁 Filter 狀態與 URL 查詢參數同步管理
 *
 * URL 範例格式：
 * - /shop?roast=medium-roast&orderby=date&order=desc
 * - /product-category/coffee?roast=light-roast&orderby=price&order=asc
 */

const useShopFilterState = (): UseShopFilterStateReturn => {
  const [searchParams] = useSearchParams();

  const { categorySlug, subCategorySlug } = useParams();
  const category = subCategorySlug || categorySlug || undefined;

  const navigate = useNavigate();

  /**
   * 從 URL 讀取 filter 狀態
   */
  const getFilterFromURL = (): ProductsQuery | null => {
    const hasFilterParams =
      searchParams.has("roast") || searchParams.has("orderby") || searchParams.has("order");

    if (!hasFilterParams && !category) return null;

    const roast = searchParams.get("roast");
    const attributes = roast ? [{ attribute: ROAST_ATTR_TAXONOMY, slug: roast }] : undefined;

    const orderbyParam = searchParams.get("orderby");
    const isValidOrderby = Object.values(SortOrderBy).includes(orderbyParam as SortOrderBy); // 驗證參數，可避免注入攻擊
    const orderby = isValidOrderby ? (orderbyParam as SortOrderBy) : SortOrderBy.DATE;

    const orderParam = searchParams.get("order");
    const isVaildOrder = Object.values(SortDirection).includes(orderParam as SortDirection);
    const order = isVaildOrder ? (orderParam as SortDirection) : SortDirection.DESC;

    return {
      category,
      attributes,
      orderby,
      order,
    };
  };

  /**
   * 將 filter 狀態同步到 URL
   */
  const saveFilterToURL = (filter: ProductsQuery) => {
    const params = new URLSearchParams(searchParams);

    const roast = filter.attributes?.[0]?.slug;
    if (roast) {
      params.set("roast", roast);
    } else {
      params.delete("roast");
    }

    // 預設值不寫入 URL，避免 URL 過長
    if (filter.orderby && filter.orderby !== SortOrderBy.DATE) {
      params.set("orderby", filter.orderby);
    } else {
      params.delete("orderby");
    }

    if (filter.order && filter.order !== SortDirection.DESC) {
      params.set("order", filter.order);
    } else {
      params.delete("order");
    }

    const newSearch = params.toString();
    const newPath = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`;
    navigate(newPath, { replace: true }); // 使用 replace 讓新網址取代目前的歷史記錄，不新增一筆記錄
  };

  return { getFilterFromURL, saveFilterToURL };
};

export default useShopFilterState;
